<template>
  <v-app>
    <v-app-bar app color="white" class="virtual-app-bar">
      <a href="/#/">
        <img src="/images/front/virtual-logo.png" class="virtual-top-logo" />
      </a>
      <v-spacer />
      <v-btn v-if="showRoomsSelect" class="mr-2" text @click="navigateTo('/')">
        <template v-if="switchMenuTitle">
          {{ switchMenuTitle }}
        </template>
        <template v-else>
          {{ $t('layout.live_broadcasting') }}
        </template>
      </v-btn>
      <v-btn
        v-if="hasShowrooms"
        text
        @click="navigateTo({ name: 'showrooms' })"
      >
        {{ $t('layout.virtual_showrooms') }}
      </v-btn>
      <v-btn
        icon
        v-for="btn in toolbarButtons"
        :key="btn.path"
        @click="navigateTo(btn.path)"
      >
        <i :class="'fal fa-' + btn.icon + ' fa-fw'"></i>
      </v-btn>

      <settings-icon/>

      <notifications v-if="showNotifications"></notifications>

      <template v-if="user">
        <div v-if="showScore" class="appbar-points">
          <v-progress-circular
            v-if="loadingTotalPoints"
            indeterminate
            color="white"
            size="22"
            width="2"
          ></v-progress-circular>
          <template v-else>
            {{ totalPoints }} {{ $t('layout.points_short') }}
          </template>
        </div>
      </template>
    </v-app-bar>

    <v-main>
      <div
        v-if="user"
        class="d-flex fill-height"
        :class="{ 'without-footer': !footer.enabled }"
      >
        <div class="flex-grow-1 pa-3" :style="{ width: leftContainerWidth }">
          <div :class="{ 'fill-height': isShowRoomModule }">
            <div class="elevation-3" :style="{ height: leftContentHeight }">
              <room-select v-if="isSelectRoomShowed" />
              <video-stream
                v-else-if="!isShowRoomModule"
                layout="virtualDesktop"
              />
              <router-view :key="$route.fullPath" name="viewer" />
            </div>

            <smileys-swiper />

            <partners-swiper
              v-if="showPartnersBar && !isShowRoomModule"
              class="pt-3"
            />
          </div>
        </div>

        <div
          v-if="isInteractiveViewVisible"
          class="interactive-view pa-3"
          :style="{ width: rightContainerWidth }"
        >
          <in-page-messages />
          <div class="interactive-container elevation-3">
            <v-navigation-drawer
              v-if="!isShowRoomModule"
              class="primary virtual-drawer"
              dark
              absolute
              :mini-variant="isMiniMenu"
              @mouseenter.native="isMiniMenu = false"
              @mouseleave.native="isMiniMenu = true"
              @click.native="isMiniMenu = false"
            >
              <v-list dense nav>
                <v-list-item
                  v-for="menuItem in menu"
                  :key="menuItem.id"
                  :disabled="!menuItem.enabled"
                  @click.stop="navigateTo(menuItem)"
                >
                  <v-list-item-action>
                    <i
                      v-if="menuItem.icon"
                      class="fal fa-fw"
                      :class="`fa-${menuItem.icon}`"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ menuItem.name }}
                      <sup v-if="menuItem.module == 'external_link'"
                        ><i
                          class="fal fa-external-link"
                          aria-hidden="true"
                          style="font-size: 100%"
                        ></i
                      ></sup>
                    </v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-action
                    v-if="
                      unreadMessagesCount != 0 && menuItem.module == 'messages'
                    "
                  >
                    <span class="badge">{{ unreadMessagesCount }}</span>
                  </v-list-item-action>

                  <v-list-item-action
                    v-if="
                      unreadNetworkingMessagesIds.length != 0 &&
                        menuItem.module == 'networking'
                    "
                  >
                    <span class="badge">{{
                      unreadNetworkingMessagesIds.length
                    }}</span>
                  </v-list-item-action>
                </v-list-item>

                <!-- <cookies/> -->

              </v-list>
            </v-navigation-drawer>

            <div
              v-if="!isShowRoomModule && menuTitle"
              class="interactive-menu-title"
            >
              {{ menuTitle }}
            </div>

            <div
              class="interactive-content"
              :style="{ left: isShowRoomModule ? 0 : null }"
            >
              <transition name="router-view">
                <router-view :key="$route.fullPath"></router-view>
              </transition>
            </div>
          </div>
        </div>
      </div>
      <login v-else />
    </v-main>
    <v-footer app inset class="pr-2" v-if="footer.enabled">
      <v-spacer />
      <span v-html="footer.content" />
    </v-footer>
  </v-app>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import Notifications from '../components/toolbar/notifications'
import PartnersSwiper from '../components/views/PartnersSwiper'
import SmileysSwiper from '../components/views/SmileysSwiper'
import Login from '../components/login'
import RoomSelect from '../components/rooms-select'
import VideoStream from '../components/video-stream'
import navigateMixin from '../mixins/navigate'
import viewportMixin from '../mixins/viewport'
import InPageMessages from '../components/in-page-messages'
import SettingsIcon from '../components/settings/icon'

export default {
  mixins: [navigateMixin, viewportMixin],

  components: {
    Notifications,
    PartnersSwiper,
    SmileysSwiper,
    Login,
    RoomSelect,
    VideoStream,
    InPageMessages,
    SettingsIcon
  },

  data() {
    return {
      isMiniMenu: true,
    }
  },

  computed: {
    ...mapGetters('common', {
      showNotifications: 'showNotifications',
      menu: 'rootMenu',
      room: 'room',
    }),

    ...mapGetters({
      toolbarButtons: 'buttons',
      unreadMessagesCount: 'unreadMessagesCount',
      unreadNetworkingMessagesIds: 'unreadNetworkingMessagesIds',
      hasShowrooms: 'showrooms/hasShowrooms',
    }),

    ...mapState({
      user: state => state.auth.user,
      totalPoints: state => state.auth.totalPoints,
      loadingTotalPoints: state => state.auth.loadingTotalPoints,
      rooms: state => state.common.rooms,
      showScore: state => state.common.virtual.showScore,
      showPartnersBar: state => state.common.virtual.partnersBar.show,
      menuTitle: state => state.common.virtual.menuTitle,
      switchMenuTitle: state => state.common.virtual.multipleRooms.menuItemName,
      footer: state => state.common.footer,
      interactiveViewHidden: state => state.common.interactiveViewHidden,
      smileysEnabled: state => state.common.virtual.smileysEnabled,
      showRoomsSelect: state => state.common.virtual.showRoomsSelect,
    }),

    isShowRoomModule() {
      return ['showroom', 'showrooms'].includes(this.$route.name)
    },

    isInteractiveViewVisible() {
      return this.$route.name != 'showrooms' && !this.interactiveViewHidden
    },

    isSelectRoomShowed() {
      return (
        this.showRoomsSelect &&
        !this.$route.params.roomId &&
        !this.isShowRoomModule
      )
    },

    leftContainerWidth() {
      if (!this.isInteractiveViewVisible) {
        return `${this.viewport.width}px`
      }

      if (this.$route.name == 'showroom') {
        const width = this.viewport.width * 0.7
        return `${width}px`
      }

      let width = (parseInt(this.leftContentHeight) / 9) * 16

      if (this.viewport.width - width < 376) {
        width = this.viewport.width - 376
      }

      return `${width}px`
    },

    rightContainerWidth() {
      const width = this.viewport.width - parseInt(this.leftContainerWidth)
      return `${width}px`
    },

    leftContentHeight() {
      const appBarHeight = 64
      const padding = 12

      const partnersHeight =
        this.showPartnersBar && !this.isShowRoomModule ? 100 : 0
      const smileysHeight = this.room && this.room.smileys_enabled ? 50 : 0
      const partnersOrSmileysHeight = smileysHeight || partnersHeight

      const footerHeight = this.footer.enabled ? 33 : 0
      const height =
        this.viewport.height -
        appBarHeight -
        2 * padding -
        partnersOrSmileysHeight -
        footerHeight

      return `${height}px`
    },
  },

  mounted() {
    if (this.user && this.showScore) {
      this.$store.dispatch('auth/getTotalPoints')
    }
  },
}
</script>
